import { PERK_TREE } from "./PerkSystem.js";
import { QuestSystem } from "./QuestSystem.js";

export class PlayerProfile {
    constructor(savedState = {}) {
        this.classId = savedState.classId || null; // 'internista', 'urgenciologo', 'cirujano'
        this.activePerks = savedState.activePerks || [];
        this.skillPoints = savedState.skillPoints || 0;
        this.dailyQuests = savedState.dailyQuests || [];
        this.lastQuestReset = savedState.lastQuestReset || null;
        this.skillCooldowns = savedState.skillCooldowns || {}; // { guardId: timestamp }
    }

    getClassDef() {
        return CLASSES[this.classId] || null;
    }

    isClassSelected() {
        return !!this.classId;
    }

    setClass(classId) {
        if (CLASSES[classId]) {
            this.classId = classId;
            return true;
        }
        return false;
    }

    generateDailyQuests() {
        const today = new Date().toISOString().slice(0, 10);
        if (this.lastQuestReset !== today) {
            this.dailyQuests = QuestSystem.generateDailyQuests(today);
            this.lastQuestReset = today;
        }
    }

    /**
     * Runs a gameplay event against today's quests, advancing progress.
     * @returns {Array} Quests that were newly completed by this event.
     */
    checkQuests(eventData) {
        const completed = [];
        (this.dailyQuests || []).forEach(q => {
            if (QuestSystem.checkProgress(q, eventData)) completed.push(q);
        });
        return completed;
    }

    // --- PERK SYSTEM ---
    canUnlockPerk(perkId, playerLevel) {
        if (this.activePerks.includes(perkId)) return false;
        const node = PERK_TREE.nodes.find(n => n.id === perkId);
        if (!node) return false;

        if (this.skillPoints < node.cost_sp) return false;
        if (playerLevel < node.requires.level) return false;

        // Check perk prerequisites
        for (const reqPerk of node.requires.perks) {
            if (!this.activePerks.includes(reqPerk)) return false;
        }

        return true;
    }

    unlockPerk(perkId, playerLevel) {
        if (!this.canUnlockPerk(perkId, playerLevel)) return false;
        const node = PERK_TREE.nodes.find(n => n.id === perkId);
        this.skillPoints -= node.cost_sp;
        this.activePerks.push(perkId);
        return true;
    }

    gainLevel(amount = 1) {
        // 1 SP per level
        this.skillPoints += amount;
    }

    exportState() {
        return {
            classId: this.classId,
            activePerks: this.activePerks,
            skillPoints: this.skillPoints,
            dailyQuests: this.dailyQuests,
            lastQuestReset: this.lastQuestReset,
            skillCooldowns: this.skillCooldowns
        };
    }
}

export const CLASSES = {
    internista: {
        id: 'internista',
        name: 'El Analista (Internista)',
        desc: 'Lectura profunda, acción pausada. Privilegia el razonamiento diagnóstico.',
        passive: { type: 'hint_unlock', scope: 'concept', desc: 'Desbloquea pista clínica desde el inicio.' },
        active: { id: 'ojo_clinico', name: 'Descarte Razonado', desc: 'Elimina 1 opción incorrecta.', uses: 1 },
        sesgo: { type: 'time_penalty', value: -5, desc: 'Sesgo de anclaje: -5s de tiempo base para decidir.' }
    },
    urgenciologo: {
        id: 'urgenciologo',
        name: 'El Táctico (Urgenciólogo)',
        desc: 'Decisión rápida, tolerancia al Caos. Prioriza soporte vital.',
        passive: { type: 'time_buffer', value: 10, desc: '+10s de tiempo base para actuar.' },
        active: { id: 'reanimacion', name: 'Soporte Avanzado', desc: 'Sobrevive a 1 error letal o grave.', uses: 1 },
        sesgo: { type: 'blind_tags', value: true, desc: 'Visión de túnel: Las etiquetas clínicas están ocultas.' }
    },
    cirujano: {
        id: 'cirujano',
        name: 'El Intervencionista (Cirujano)',
        desc: 'Mano firme, alto riesgo. Orientado a la resolución definitiva.',
        passive: { type: 'credit_bonus', value: 0.25, condition: 'time<50%', desc: '+25% Créditos si respondes rápido.' },
        active: { id: 'corte_preciso', name: 'Decisión Crítica', desc: 'Fuerza la resolución rapida.', uses: 1 },
        sesgo: { type: 'damage_multiplier', value: 1.5, desc: 'Exceso de confianza: Los errores cuestan 1.5x de estabilidad.' }
    }
};
